/*
 * Carbisdale - CoreModel
 *
 * Datenmodell fuer die Core Console.
 * Verwaltet Entries (System-Logs, User-Input, AI-Antworten),
 * die aktuelle Eingabe und den Lock-Zustand.
 */

import {
    getWordBounds,
    codepointLengthAt, codepointLengthBefore
} from '../TextProcessor.js'
import {
    adjustSpansForInsert, adjustSpansForDelete, adjustSpansForDeleteRange,
    extendOrCreateSpan, removeFormatFromRange, isRangeCovered,
    getFormatsBeforeOffset, richTextToMarkdown
} from '../RichTextOps.js'
import { parseMarkdownBlocks } from '../MarkdownBlockParser.js'

/** @private — verwendet von takeSnapshot zum Dedup-Vergleich. */
function _formatsEqual(a, b) {
    if (a === b) return true
    if (!a || !b || a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) {
        const sa = a[i], sb = b[i]
        if (sa.start !== sb.start || sa.end !== sb.end || sa.type !== sb.type) return false
    }
    return true
}

/** @private — Set-Vergleich für activeFormats im Snapshot-Dedup. */
function _activeFormatsEqual(a, b) {
    if (a === b) return true
    if (!a || !b || a.size !== b.size) return false
    for (const f of a) if (!b.has(f)) return false
    return true
}

/**
 * Ein einzelner Console-Eintrag.
 * text und collapsed koennen per appendToEntry/setEntryCollapsed geaendert werden.
 */
export class ConsoleEntry {
    /**
     * @param {number} id - Fortlaufende ID
     * @param {string} type - 'SYSTEM' | 'USER' | 'AI' | 'ERROR'
     * @param {string} text - Inhalt (Plain-Text oder Markdown-Inline)
     */
    constructor(id, type, text) {
        this.id = id
        this.type = type
        this.text = text
        this.timestamp = new Date()
        this.collapsed = false
    }
}

// Dirty-Level: Was hat sich geaendert?
// Hierarchie (hoechste gewinnt): STRUCTURAL > INPUT > SELECTION > CLEAN
export const DirtyLevel = Object.freeze({
    CLEAN: 'CLEAN',
    SELECTION: 'SELECTION',     // Nur Output-Selection — kein Rebuild, kein Scroll
    INPUT: 'INPUT',             // Nur Eingabezeile
    STRUCTURAL: 'STRUCTURAL'    // Entries geaendert, Rebuild noetig
})

/**
 * CoreModel — Datenmodell der Console.
 */
export class CoreModel {
    constructor() {
        this.entries = []
        this.inputText = ''
        this.inputCursor = 0
        this.inputSelection = null  // {anchor, head} oder null
        // Selection ueber Output-Entries (USER/AI/THINKING/SYSTEM/ERROR/REVIEW_PANEL).
        // Format: { anchor: {entryIndex, offset}, head: {entryIndex, offset} } | null
        // Schliesst sich gegenseitig mit inputSelection aus (Mutex).
        this.outputSelection = null
        this.locked = false
        this.nextId = 1

        // Rich-Text Format-Spans fuer den Input
        this.inputFormats = []        // FormatSpan[] — {start, end, type}
        this._activeFormats = new Set()  // Aktive Formate bei Cursor-Position (ohne Wort)

        this._dirtyLevel = DirtyLevel.CLEAN
        this._actionBlocked = false
        // Wenn true, soll der naechste STRUCTURAL-Rebuild die Scroll-Position
        // erhalten (kein scrollToBottom). Wird von setEntryCollapsed/
        // toggleEntryCollapsed gesetzt; `consumePreserveScroll()` liest + reset.
        this._preserveScrollOnDirty = false

        // Undo/Redo fuer Input (Snapshot-basiert)
        this._undoStack = []
        this._redoStack = []

        // Externe Edit-Gruppe (beginEditGroup/endEditGroup): waehrend einer
        // offenen Gruppe deckt EIN initialer takeSnapshot() die gesamte
        // Sequenz ab. Use-Case: Diktat — `insertAtCursor` pusht selbst
        // keinen Snapshot, ohne diese Gruppe landet das ganze Diktat
        // ueberhaupt nicht im Undo-Stack.
        this._inEditGroup = false
    }

    // === Entries ===

    /**
     * Fuegt einen neuen Eintrag hinzu.
     * @param {string} type - 'SYSTEM' | 'USER' | 'AI' | 'ERROR'
     * @param {string} text - Inhalt
     * @returns {ConsoleEntry} Der neue Eintrag
     */
    addEntry(type, text) {
        const entry = new ConsoleEntry(this.nextId++, type, text)
        this.entries.push(entry)
        this.clearOutputSelection()
        this._markDirty(DirtyLevel.STRUCTURAL)
        return entry
    }

    /** @returns {ConsoleEntry[]} Alle Eintraege */
    getEntries() {
        return this.entries
    }

    /** @returns {number} Naechste Entry-ID (fuer Projekt-Swap persistiert) */
    getNextId() {
        return this.nextId
    }

    /**
     * Ersetzt das Entries-Array (fuer Projekt-Swap in der Console).
     * Nimmt optional auch die naechste ID, damit pro Projekt die ID-Sequenz
     * stabil bleibt und Collapsed-States per ID auflösbar sind.
     * @param {ConsoleEntry[]} entries
     * @param {number} [nextId]
     */
    setEntries(entries, nextId) {
        this.entries = entries
        if (typeof nextId === 'number' && nextId > 0) {
            this.nextId = nextId
        }
        this.clearOutputSelection()
        this._markDirty(DirtyLevel.STRUCTURAL)
    }

    /**
     * Entfernt einen Entry per ID.
     * @param {number} entryId
     */
    removeEntry(entryId) {
        const idx = this.entries.findIndex(e => e.id === entryId)
        if (idx !== -1) {
            this.entries.splice(idx, 1)
            this.clearOutputSelection()
            this._markDirty(DirtyLevel.STRUCTURAL)
        }
    }

    /**
     * Findet einen Entry per ID.
     * @param {number} entryId
     * @returns {ConsoleEntry|undefined}
     */
    _getEntry(entryId) {
        return this.entries.find(e => e.id === entryId)
    }

    /**
     * Haengt Text an einen bestehenden Entry an (fuer Streaming).
     * @param {number} entryId
     * @param {string} text - Anzuhaengender Text
     */
    appendToEntry(entryId, text) {
        const entry = this._getEntry(entryId)
        if (entry) {
            entry.text += text
            // Streaming-Append invalidiert Output-Selection (Boris' Wahl: simpler).
            this.clearOutputSelection()
            this._markDirty(DirtyLevel.STRUCTURAL)
        }
    }

    /**
     * Setzt den Collapsed-Zustand eines Entries.
     * @param {number} entryId
     * @param {boolean} collapsed
     */
    setEntryCollapsed(entryId, collapsed) {
        const entry = this._getEntry(entryId)
        if (entry && entry.collapsed !== collapsed) {
            entry.collapsed = collapsed
            this._preserveScrollOnDirty = true
            this._markDirty(DirtyLevel.STRUCTURAL)
        }
    }

    /**
     * Toggled den Collapsed-Zustand eines Entries.
     * @param {number} entryId
     */
    toggleEntryCollapsed(entryId) {
        const entry = this._getEntry(entryId)
        if (entry) {
            entry.collapsed = !entry.collapsed
            this._preserveScrollOnDirty = true
            this._markDirty(DirtyLevel.STRUCTURAL)
        }
    }

    /**
     * Liest das preserve-scroll-Flag und setzt es zurueck. Wird vom Render-
     * Pfad konsultiert, damit Collapse/Expand den Viewport nicht ans Ende
     * zieht.
     * @returns {boolean}
     */
    consumePreserveScroll() {
        const val = this._preserveScrollOnDirty
        this._preserveScrollOnDirty = false
        return val
    }

    /** @returns {number} Anzahl Eintraege */
    getEntryCount() {
        return this.entries.length
    }

    // === Input ===

    /** @returns {string} Aktueller Eingabetext */
    getInputText() {
        return this.inputText
    }

    /**
     * Setzt den gesamten Eingabetext.
     * @param {string} text
     */
    setInputText(text) {
        this.inputText = text
        this.clearOutputSelection()
        this._markDirty(DirtyLevel.INPUT)
    }

    /** @returns {number} Cursor-Position im Input */
    getInputCursor() {
        return this.inputCursor
    }

    /**
     * Setzt die Cursor-Position.
     * @param {number} pos - Position (0..inputText.length)
     */
    setInputCursor(pos) {
        this.inputCursor = Math.max(0, Math.min(pos, this.inputText.length))
        this.clearOutputSelection()
        this._markDirty(DirtyLevel.INPUT)
    }

    /** @returns {{anchor: number, head: number}|null} Aktuelle Selection */
    getInputSelection() {
        return this.inputSelection
    }

    /**
     * Setzt die Input-Selection.
     * @param {{anchor: number, head: number}|null} sel
     */
    setInputSelection(sel) {
        this.inputSelection = sel
        // Mutex: Output-Selection wird verworfen, sobald Input fokussiert wird.
        if (sel !== null) this.clearOutputSelection()
        this._markDirty(DirtyLevel.INPUT)
    }

    /** Loescht die Selection. */
    clearInputSelection() {
        this.inputSelection = null
        this._markDirty(DirtyLevel.INPUT)
    }

    /** @returns {boolean} Ob eine Selection existiert */
    hasSelection() {
        return this.inputSelection !== null &&
            this.inputSelection.anchor !== this.inputSelection.head
    }

    /**
     * Gibt den selektierten Text zurueck.
     * @returns {string}
     */
    getSelectedText() {
        if (!this.hasSelection()) return ''
        const sel = this.inputSelection
        const start = Math.min(sel.anchor, sel.head)
        const end = Math.max(sel.anchor, sel.head)
        return this.inputText.substring(start, end)
    }

    // === Output Selection (Selektion ueber Console-Entries) ===

    /**
     * Output-Selection-Position: Tripel `(entryIndex, blockId, offset)`.
     * Bei AI-Entries unterscheidet `blockId` zwischen Markdown-Bloecken
     * (Heading/Paragraph/Code/...) — `offset` ist dann block-relativ.
     * Bei non-AI-Entries (USER/SYSTEM/ERROR/THINKING) gibt es nur einen
     * impliziten Block, blockId bleibt `-1` und `offset` ist entry-relativ.
     * Sortierung in Doc-Order ist lex. ueber die drei Felder.
     * @returns {{anchor: {entryIndex, blockId, offset}, head: {entryIndex, blockId, offset}}|null}
     */
    getOutputSelection() {
        return this.outputSelection
    }

    /**
     * Vergleicht zwei Selection-Positionen in Doc-Order.
     * @returns {number} -1, 0, 1
     */
    static _comparePos(a, b) {
        if (a.entryIndex !== b.entryIndex) return a.entryIndex < b.entryIndex ? -1 : 1
        if (a.blockId !== b.blockId) return a.blockId < b.blockId ? -1 : 1
        if (a.offset !== b.offset) return a.offset < b.offset ? -1 : 1
        return 0
    }

    /**
     * Setzt die Output-Selection (clear automatisch die Input-Selection).
     * Markiert SELECTION dirty: Lines aendern sich nicht, der Render-Pfad
     * zeichnet nur neu — kein rebuildLines, kein scrollToBottom.
     * @param {{anchor: {entryIndex, blockId, offset}, head: {entryIndex, blockId, offset}}|null} sel
     */
    setOutputSelection(sel) {
        // blockId-Default: AI-Entries ohne explizite Block-Info bekommen
        // Block 0 (legacy: ganzer Entry-Text als ein Block-Aequivalent),
        // non-AI-Entries bekommen -1 (Single-Block, entry-relative offsets).
        if (sel !== null) {
            if (sel.anchor && sel.anchor.blockId === undefined) {
                sel.anchor.blockId = this._defaultBlockIdFor(sel.anchor.entryIndex)
            }
            if (sel.head && sel.head.blockId === undefined) {
                sel.head.blockId = this._defaultBlockIdFor(sel.head.entryIndex)
            }
        }
        this.outputSelection = sel
        // Mutex: Output-Selection und Input-Selection schliessen sich aus.
        if (sel !== null && this.inputSelection !== null) {
            this.inputSelection = null
        }
        this._markDirty(DirtyLevel.SELECTION)
    }

    /** @private — Default-blockId pro Entry-Type (AI=0, sonst -1). */
    _defaultBlockIdFor(entryIndex) {
        const e = this.entries[entryIndex]
        return (e && e.type === 'AI') ? 0 : -1
    }

    /** Loescht die Output-Selection (no-op wenn schon null). */
    clearOutputSelection() {
        if (this.outputSelection !== null) {
            this.outputSelection = null
            this._markDirty(DirtyLevel.SELECTION)
        }
    }

    /** @returns {boolean} Ob eine nicht-leere Output-Selection existiert */
    hasOutputSelection() {
        if (this.outputSelection === null) return false
        const { anchor, head } = this.outputSelection
        return CoreModel._comparePos(anchor, head) !== 0
    }

    /**
     * Normalisiert die Output-Selection so, dass anchor <= head (in Doc-Order).
     * @returns {{anchor: {entryIndex, blockId, offset}, head: {entryIndex, blockId, offset}}|null}
     */
    getNormalizedOutputSelection() {
        if (!this.hasOutputSelection()) return null
        const { anchor, head } = this.outputSelection
        const swap = CoreModel._comparePos(anchor, head) > 0
        return swap ? { anchor: head, head: anchor } : { anchor, head }
    }

    /**
     * Liefert den selektierten Text ueber alle erfassten Entries und Bloecke.
     * Trenner zwischen Bloecken/Entries: "\n\n". Collapsed THINKING-Entries
     * werden uebersprungen.
     * @returns {string}
     */
    getSelectedOutputText() {
        const sel = this.getNormalizedOutputSelection()
        if (!sel) return ''
        const parts = []
        for (let i = sel.anchor.entryIndex; i <= sel.head.entryIndex; i++) {
            const e = this.entries[i]
            if (!e) continue
            if (e.type === 'THINKING' && e.collapsed) continue

            // AI-Entry mit Markdown-Bloecken: pro Block sliceeen
            if (e.type === 'AI') {
                const blocks = parseMarkdownBlocks(e.text)
                const firstBlock = (i === sel.anchor.entryIndex) ? sel.anchor.blockId : 0
                const lastBlock = (i === sel.head.entryIndex) ? sel.head.blockId : blocks.length - 1
                for (let b = firstBlock; b <= lastBlock; b++) {
                    const block = blocks[b]
                    if (!block) continue
                    const txt = block.text || ''
                    const start = (i === sel.anchor.entryIndex && b === sel.anchor.blockId) ? sel.anchor.offset : 0
                    const end = (i === sel.head.entryIndex && b === sel.head.blockId) ? sel.head.offset : txt.length
                    if (end > start) {
                        const sub = txt.substring(start, end)
                        // Code-Blöcke vollständig selektiert → in ``` einwickeln,
                        // damit der Editor-Paste sie wieder zu codeBlock-Paragraphs
                        // erkennt. Teilauswahl bleibt raw (nur der Ausschnitt).
                        if (block.type === 'code' && start === 0 && end === txt.length) {
                            parts.push('```\n' + sub + '\n```')
                        } else {
                            parts.push(sub)
                        }
                    }
                }
                continue
            }

            // Non-AI-Entry: ein impliziter Block, offset ist entry-relativ
            const start = (i === sel.anchor.entryIndex) ? sel.anchor.offset : 0
            const end = (i === sel.head.entryIndex) ? sel.head.offset : e.text.length
            if (end > start) parts.push(e.text.substring(start, end))
        }
        // Single Newline zwischen Blöcken/Entries — sonst entstehen beim Paste in
        // den Editor Leerzeilen, weil dort `\n` schon eine Absatzgrenze ist.
        return parts.join('\n')
    }

    /**
     * Loescht die selektierte Text-Passage.
     * Cursor wird an den Start der Selection gesetzt.
     */
    deleteSelection() {
        if (!this.hasSelection()) return
        const sel = this.inputSelection
        const start = Math.min(sel.anchor, sel.head)
        const end = Math.max(sel.anchor, sel.head)
        this.inputText = this.inputText.substring(0, start) + this.inputText.substring(end)
        this.inputFormats = adjustSpansForDeleteRange(this.inputFormats, start, end)
        this.inputCursor = start
        this.inputSelection = null
        this.clearOutputSelection()
        this._markDirty(DirtyLevel.INPUT)
    }

    /**
     * Fuegt Text an der Cursor-Position ein.
     * Loescht vorher die Selection falls vorhanden.
     * @param {string} text
     */
    insertAtCursor(text) {
        if (this.hasSelection()) {
            this.deleteSelection()
        }
        this.clearOutputSelection()
        const insertOffset = this.inputCursor
        const before = this.inputText.substring(0, insertOffset)
        const after = this.inputText.substring(insertOffset)
        this.inputText = before + text + after

        // Spans anpassen
        this.inputFormats = adjustSpansForInsert(this.inputFormats, insertOffset, text.length)

        // Aktive Formate auf den neuen Text anwenden
        for (const type of this._activeFormats) {
            this.inputFormats = extendOrCreateSpan(this.inputFormats, insertOffset, insertOffset + text.length, type)
        }

        this.inputCursor = insertOffset + text.length
        this._markDirty(DirtyLevel.INPUT)
    }

    /**
     * Loescht ein Zeichen relativ zum Cursor.
     * @param {number} direction - -1 fuer Backspace, +1 fuer Delete
     * @returns {boolean} true wenn etwas geloescht wurde
     */
    deleteAtCursor(direction) {
        if (this.hasSelection()) {
            this.deleteSelection()
            return true
        }
        this.clearOutputSelection()
        if (direction === -1) {
            // Backspace (Surrogate Pair: ganzen Codepoint löschen)
            if (this.inputCursor === 0) return false
            const cpLen = codepointLengthBefore(this.inputText, this.inputCursor)
            const deleteOffset = this.inputCursor - cpLen
            this.inputText = this.inputText.substring(0, deleteOffset) +
                this.inputText.substring(this.inputCursor)
            // Spans anpassen (pro gelöschtes Zeichen, von hinten nach vorne)
            for (let i = cpLen - 1; i >= 0; i--) {
                this.inputFormats = adjustSpansForDelete(this.inputFormats, deleteOffset + i)
            }
            this.inputCursor = deleteOffset
            this._markDirty(DirtyLevel.INPUT)
            return true
        } else {
            // Delete (Surrogate Pair: ganzen Codepoint löschen)
            if (this.inputCursor >= this.inputText.length) return false
            const cpLen = codepointLengthAt(this.inputText, this.inputCursor)
            this.inputText = this.inputText.substring(0, this.inputCursor) +
                this.inputText.substring(this.inputCursor + cpLen)
            // Spans anpassen (pro gelöschtes Zeichen, von hinten nach vorne)
            for (let i = cpLen - 1; i >= 0; i--) {
                this.inputFormats = adjustSpansForDelete(this.inputFormats, this.inputCursor + i)
            }
            this._markDirty(DirtyLevel.INPUT)
            return true
        }
    }

    /**
     * Leert die Eingabe (nach Absenden).
     */
    clearInput() {
        this.inputText = ''
        this.inputCursor = 0
        this.inputSelection = null
        this.inputFormats = []
        this._activeFormats.clear()
        this._undoStack = []
        this._redoStack = []
        this.clearOutputSelection()
        this._markDirty(DirtyLevel.INPUT)
    }

    /**
     * Sendet die aktuelle Eingabe ab.
     * Erzeugt einen USER-Entry und leert die Eingabe.
     * @returns {string|null} Der abgesendete Text oder null wenn leer/gesperrt
     */
    submitInput() {
        if (this.locked) return null
        const text = this.inputText.trim()
        if (!text) return null

        // Markdown-Text fuer den AgentService erzeugen
        const markdownText = this.inputFormats.length > 0
            ? richTextToMarkdown(this.inputText, this.inputFormats, [])
            : this.inputText

        // Markdown-Text als USER-Entry hinzufuegen (für Rendering)
        this.addEntry('USER', markdownText)
        this.clearInput()
        return markdownText
    }

    // === Format ===

    /**
     * Wendet Inline-Formatierung auf die Selection oder das Wort unter dem Cursor an.
     * RT-basiert: Arbeitet mit inputFormats (FormatSpan[]) statt Markdown-Markern.
     * @param {string} format - 'bold', 'italic', 'strikethrough', 'underline', 'code'
     */
    applyFormat(format) {
        this._actionBlocked = false
        if (this.hasSelection()) {
            this._applyFormatToSelection(format)
        } else {
            this._toggleFormatAtCursor(format)
        }
        this._markDirty(DirtyLevel.INPUT)
    }

    /**
     * Wendet Format auf Selection an (Toggle).
     * @param {string} format - 'bold', 'italic', 'strikethrough', 'underline', 'code'
     */
    _applyFormatToSelection(format) {
        const sel = this.inputSelection
        if (!sel) return

        const start = Math.min(sel.anchor, sel.head)
        const end = Math.max(sel.anchor, sel.head)

        const covered = isRangeCovered(this.inputFormats, start, end, format)
        if (covered) {
            this.inputFormats = removeFormatFromRange(this.inputFormats, start, end, format)
        } else {
            this.inputFormats = extendOrCreateSpan(this.inputFormats, start, end, format)
        }
    }

    /**
     * Togglet Format am Wort unter dem Cursor (ohne Selection).
     * @param {string} format - 'bold', 'italic', 'strikethrough', 'underline', 'code'
     */
    _toggleFormatAtCursor(format) {
        const wordBounds = getWordBounds(this.inputText, this.inputCursor)

        // Sonderfall: Cursor am Wortende, Format aktiv, Span endet hier
        if (wordBounds && this.inputCursor === wordBounds.end && this._activeFormats.has(format)) {
            const spanEndsHere = this.inputFormats.some(s => s.type === format && s.end === this.inputCursor)
            if (spanEndsHere) {
                this._activeFormats.delete(format)
                return
            }
        }

        if (wordBounds && wordBounds.start < wordBounds.end) {
            const covered = isRangeCovered(this.inputFormats, wordBounds.start, wordBounds.end, format)
            if (covered) {
                this.inputFormats = removeFormatFromRange(this.inputFormats, wordBounds.start, wordBounds.end, format)
            } else {
                this.inputFormats = extendOrCreateSpan(this.inputFormats, wordBounds.start, wordBounds.end, format)
            }
            return
        }

        // Kein Wort unter Cursor: _activeFormats togglen
        if (this._activeFormats.has(format)) {
            this._activeFormats.delete(format)
        } else {
            this._activeFormats.add(format)
        }
    }

    /**
     * Aktualisiert _activeFormats basierend auf der Cursor-Position.
     * Wird nach Cursor-Bewegung aufgerufen.
     */
    _updateActiveFormats() {
        this._activeFormats = getFormatsBeforeOffset(this.inputFormats, this.inputCursor)
    }

    /** @returns {FormatSpan[]} Aktuelle Input-Format-Spans */
    getInputFormats() {
        return this.inputFormats
    }

    /** @returns {boolean} Ob die letzte applyFormat-Aktion blockiert wurde */
    isActionBlocked() {
        return this._actionBlocked
    }

    // === Lock ===

    /** Sperrt die Eingabe (AI denkt). */
    lock() {
        this.locked = true
        this._markDirty(DirtyLevel.INPUT)
    }

    /** Entsperrt die Eingabe. */
    unlock() {
        this.locked = false
        this._markDirty(DirtyLevel.INPUT)
    }

    /** @returns {boolean} Ob die Eingabe gesperrt ist */
    isLocked() {
        return this.locked
    }

    // === Undo/Redo (nur fuer Input) ===

    /**
     * Erstellt einen Undo-Snapshot des aktuellen Input-Zustands.
     * Dedup gegen den letzten Stack-Eintrag (echte No-Op-Kompression),
     * nicht gegen einen separaten Cache — sonst geht der initiale leere Zustand
     * verloren (Bug: Paste in leeren Input → Pre-Paste-State nicht im Stack).
     * Format-only-Änderungen (gleicher Text, andere formats/activeFormats) werden
     * jetzt ebenfalls eigenständig gesnapshottet.
     */
    takeSnapshot() {
        const top = this._undoStack[this._undoStack.length - 1]
        if (top
            && top.text === this.inputText
            && top.cursor === this.inputCursor
            && _formatsEqual(top.formats, this.inputFormats)
            && _activeFormatsEqual(top.activeFormats, this._activeFormats)
        ) {
            return
        }
        this._undoStack.push(this._captureInputSnapshot())
        this._redoStack = []
    }

    /** @private */
    _captureInputSnapshot() {
        return {
            text: this.inputText,
            cursor: this.inputCursor,
            selection: this.inputSelection ? { ...this.inputSelection } : null,
            formats: this.inputFormats.map(s => ({ ...s })),
            activeFormats: new Set(this._activeFormats)
        }
    }

    /**
     * Beginnt eine konsolidierte Edit-Gruppe: nimmt einmal einen Snapshot
     * vor der ersten Aenderung, danach laufen alle Aenderungen ohne neuen
     * Undo-Push. Idempotent — mehrfaches `beginEditGroup()` ist no-op.
     *
     * Use-Case: Diktat. `insertAtCursor` allein pusht keinen Snapshot,
     * also gingen Diktat-Inserts bisher ueberhaupt nicht ins Undo. Mit
     * dieser Gruppe deckt EIN Snapshot (vor dem ersten Interim) die ganze
     * Utterance ab → ein Cmd+Z entfernt die komplette Utterance.
     */
    beginEditGroup() {
        if (this._inEditGroup) return
        this.takeSnapshot()
        this._inEditGroup = true
    }

    /**
     * Beendet die laufende Edit-Gruppe. No-op wenn keine offen.
     * Folgende Aktionen koennen wieder per `takeSnapshot()` pushen.
     */
    endEditGroup() {
        if (!this._inEditGroup) return
        this._inEditGroup = false
    }

    /** Stellt den vorherigen Input-Zustand her. */
    undo() {
        if (this._undoStack.length === 0) return false

        // Aktuellen Zustand auf Redo-Stack
        this._redoStack.push(this._captureInputSnapshot())

        const snapshot = this._undoStack.pop()
        this.inputText = snapshot.text
        this.inputCursor = snapshot.cursor
        this.inputSelection = snapshot.selection
        this.inputFormats = snapshot.formats || []
        this._activeFormats = snapshot.activeFormats || new Set()
        this._markDirty(DirtyLevel.INPUT)
        return true
    }

    /** Stellt einen Redo-Zustand her. */
    redo() {
        if (this._redoStack.length === 0) return false

        // Aktuellen Zustand auf Undo-Stack
        this._undoStack.push(this._captureInputSnapshot())

        const snapshot = this._redoStack.pop()
        this.inputText = snapshot.text
        this.inputCursor = snapshot.cursor
        this.inputSelection = snapshot.selection
        this.inputFormats = snapshot.formats || []
        this._activeFormats = snapshot.activeFormats || new Set()
        this._markDirty(DirtyLevel.INPUT)
        return true
    }

    // === Dirty Tracking ===

    /** @returns {boolean} Ob sich etwas geaendert hat */
    isDirty() {
        return this._dirtyLevel !== DirtyLevel.CLEAN
    }

    /** @returns {string} Aktueller Dirty-Level */
    getDirtyLevel() {
        return this._dirtyLevel
    }

    /** Markiert als sauber. */
    clearDirty() {
        this._dirtyLevel = DirtyLevel.CLEAN
    }

    /** Erzwingt kompletten Rebuild (z.B. nach Font-Wechsel). */
    invalidateLayout() {
        this._markDirty(DirtyLevel.STRUCTURAL)
    }

    /**
     * Setzt den Dirty-Level (nur erhoehen, nie senken).
     * Hierarchie: STRUCTURAL > INPUT > SELECTION > CLEAN.
     * @param {string} level
     */
    _markDirty(level) {
        if (level === DirtyLevel.STRUCTURAL) {
            this._dirtyLevel = DirtyLevel.STRUCTURAL
        } else if (level === DirtyLevel.INPUT &&
                   this._dirtyLevel !== DirtyLevel.STRUCTURAL) {
            this._dirtyLevel = DirtyLevel.INPUT
        } else if (level === DirtyLevel.SELECTION &&
                   this._dirtyLevel === DirtyLevel.CLEAN) {
            this._dirtyLevel = DirtyLevel.SELECTION
        }
    }
}
